import {union, head} from '@shared/helpers/array'
import {lodashFind as find} from '@shared/helpers/collection'
import {eq, equals, isUndefined} from '@shared/helpers/lang'
import {mapValueKeys, someValueKeys} from '@shared/helpers/object'

import {
    CouponDiscountType,
    CouponProviderType,
    DiscountCoupon,
    DiscountCouponType,
    DiscountPolicy,
    ExceptionProductTypesByStoreCoupon,
    ImmediateDiscount,
    ImmediateDiscountType,
} from '@web/core/constants'
import {getCouponDiscountValue} from '@web/helpers/policies'
import {DeliveryGroup} from '@web/slices/deliveryGroup/slice'
import {OrderSheetCoupon, OrderSheetDiscountItem, OrderSheetImmediateCoupon} from '@web/slices/orderSheetCoupon/slice'
import {CouponOrderSheetItemFlatMapType, OrderSheetProductView} from '@web/slices/orderSheetProducts/slice'

// export const isAmountDiscount = eq(DiscountPolicy.AMOUNT)
export const isMerchantImmediateDiscount = eq(DiscountCoupon.SELLER_IMMEDIATE_DISCOUNT)
export const isMerchantProductDiscount = eq(DiscountCoupon.SELLER_PRODUCT_DISCOUNT)
export const isManagerImmediateDiscount = eq(DiscountCoupon.MANAGER_IMMEDIATE_DISCOUNT)
export const isManagerProductDiscount = eq(DiscountCoupon.MANAGER_PRODUCT_DISCOUNT)

export const eqMerchantImmediateDiscount = ({couponType}: OrderSheetCoupon | OrderSheetImmediateCoupon) =>
    isMerchantImmediateDiscount(couponType)
export const eqMerchantProductDiscount = ({couponType}: OrderSheetCoupon | OrderSheetImmediateCoupon) =>
    isMerchantProductDiscount(couponType)
export const eqManagerImmediateDiscount = ({couponType}: OrderSheetCoupon | OrderSheetImmediateCoupon) =>
    isManagerImmediateDiscount(couponType)
export const eqManagerProductDiscount = ({couponType}: OrderSheetCoupon | OrderSheetImmediateCoupon) =>
    isManagerProductDiscount(couponType)

export const makeSelectedCouponDiscountAmount =
    (productDiscountAmountInfoByItemId: {
        [OrderSheetItem: string]: {
            /** 적용 금액 */
            baseAmount: number
            /** 할인된 금액 */
            discountAmount: number
            /** 할인 유형 */
            discountType: CouponDiscountType
            /** 즉시할인 발행처 유형 */
            discountProviderType: CouponProviderType
            /** FE 쿠폰 구분 Type */
            couponType: DiscountCouponType
        }[]
    }) =>
    (itemId: string | number, couponTypeCheckFunction: (couponType: DiscountCouponType) => boolean): number => {
        return (
            productDiscountAmountInfoByItemId[itemId]?.find(({couponType}) => couponTypeCheckFunction(couponType))
                ?.discountAmount ?? 0
        )
    }

const convertCouponByImmediateCoupon = ({
    discountType,
    discountProviderType,
    policyNo,
    discountUnitType,
    discountValue,
    couponType,
    immediateDiscountType,
    maxDiscountAmount,
}: OrderSheetImmediateCoupon): OrderSheetCoupon => {
    const CouponNameMap: Record<ImmediateDiscountType, string> = {
        [ImmediateDiscount.IMMEDIATE]: '즉시 할인',
        [ImmediateDiscount.LIVE]: '라이브 할인',
        [ImmediateDiscount.PROMOTION]: '즉시 할인',
    }

    return {
        couponId: policyNo,
        couponName: CouponNameMap[immediateDiscountType],
        couponPublicationId: '',
        validityPeriodStartDateTime: '',
        validityPeriodEndDateTime: '',
        discountUnitType,
        discountValue,
        maxDiscountAmount,
        minOrderAmount: null,
        disabled: false,
        couponProviderType: discountProviderType,
        couponDiscountType: discountType,
        couponType,
    }
}

export const getProductCouponDiscount = ({
    immediateCoupon,
    immediateDiscountAmount,
    productCouponList,
    selectedImmediateCoupon,
    selectedProductCoupon,
    itemId,
    couponName,
    isImmediateDiscount,
    isProductDiscount,
    getSelectedCouponDiscountAmount,
}: {
    immediateCoupon?: OrderSheetImmediateCoupon
    immediateDiscountAmount: number
    productCouponList: OrderSheetCoupon[]
    selectedImmediateCoupon?: OrderSheetImmediateCoupon
    selectedProductCoupon?: OrderSheetCoupon
    itemId: string | number
    couponName?: string
    isImmediateDiscount: (couponType: DiscountCouponType) => boolean
    isProductDiscount: (couponType: DiscountCouponType) => boolean
    getSelectedCouponDiscountAmount: (
        itemId: string | number,
        couponTypeCheckFunction: (couponType: DiscountCouponType) => boolean,
    ) => number
}) => {
    return {
        coupons: [
            ...(immediateCoupon
                ? [
                      {
                          ...convertCouponByImmediateCoupon(immediateCoupon),
                          discountAmount: immediateDiscountAmount,
                      },
                  ]
                : []),
            ...productCouponList.map((productCoupon) => {
                const {discountValue, discountUnitType} = productCoupon

                return {
                    ...productCoupon,
                    ...(couponName ? {couponName} : {}),
                    discountAmount: eq(DiscountPolicy.AMOUNT)(discountUnitType) ? discountValue : undefined,
                }
            }),
        ],
        selectedCoupon: selectedImmediateCoupon
            ? {
                  ...convertCouponByImmediateCoupon(selectedImmediateCoupon),
                  discountAmount: getSelectedCouponDiscountAmount(itemId, isImmediateDiscount),
              }
            : selectedProductCoupon
              ? {
                    ...selectedProductCoupon,
                    discountAmount: getSelectedCouponDiscountAmount(itemId, isProductDiscount),
                }
              : null,
    }
}

const getImmediateDiscountAmount = ({
    immediateDiscount,
    unitPrice,
    quantity,
}: {
    immediateDiscount?: OrderSheetCoupon & {
        discountAmount?: number
    }
    unitPrice: number
    quantity: number
}): {
    discountUnit: number
    immediateDiscountAmount: number
} => {
    if (!immediateDiscount) {
        return {
            discountUnit: 0,
            immediateDiscountAmount: 0,
        }
    }
    if (immediateDiscount.couponType) {
        const discountUnit = getCouponDiscountValue(
            {
                couponType: immediateDiscount.couponType,
                discountUnitType: immediateDiscount.discountUnitType,
                discountValue: immediateDiscount.discountValue,
                maxDiscountAmount: immediateDiscount.maxDiscountAmount,
            },
            unitPrice,
        )
        return {
            discountUnit,
            immediateDiscountAmount: discountUnit * quantity,
        }
    }
    return {
        discountUnit: immediateDiscount.discountValue,
        immediateDiscountAmount: immediateDiscount.discountValue,
    }
}

export const getMerchantAndManagerCoupons = ({
    productCouponsByItemId,
    selectedProductCouponsByItemId,
    immediatedCouponsByItemId,
    selectedImmediateCouponsByItemId,
    orderSheetItemFlatMap,
    getSelectedCouponDiscountAmount,
}: {
    productCouponsByItemId: {[x: string]: (OrderSheetCoupon & {disabled: boolean})[]}
    selectedProductCouponsByItemId: {[OrderSheetItemId: string]: OrderSheetCoupon[]}
    immediatedCouponsByItemId: {[OrderSheetItemId: string]: OrderSheetImmediateCoupon[]}
    selectedImmediateCouponsByItemId: {[OrderSheetItemId: string]: OrderSheetImmediateCoupon[]}
    orderSheetItemFlatMap: CouponOrderSheetItemFlatMapType
    getSelectedCouponDiscountAmount: (
        itemId: string | number,
        couponTypeCheckFunction: (couponType: DiscountCouponType) => boolean,
    ) => number
}) => {
    const orderSheetItemIds = union(Object.keys(productCouponsByItemId), Object.keys(immediatedCouponsByItemId))

    return orderSheetItemIds.reduce<{
        [orderSheetItemId: string]: {
            merchant: {
                coupons: (OrderSheetCoupon & {discountAmount?: number})[]
                selectedCoupon: Maybe<OrderSheetCoupon & {discountAmount?: number}>
            }
            manager: {
                coupons: (OrderSheetCoupon & {discountAmount?: number})[]
                selectedCoupon: Maybe<OrderSheetCoupon & {discountAmount?: number}>
            }
        }
    }>((acc, orderSheetItemId) => {
        const productCoupons = productCouponsByItemId[orderSheetItemId] ?? []

        const immediatedCoupons = immediatedCouponsByItemId[String(orderSheetItemId)]
        const selectedImmediateCoupons = selectedImmediateCouponsByItemId[orderSheetItemId]
        const selectedProductCoupons = selectedProductCouponsByItemId[orderSheetItemId]

        /** 즉시할인 쿠폰은 쿠폰 리스트 중 한개만 존재 가능하므로 find로 탐색  */
        const merchantImmediateCoupon = find(immediatedCoupons, eqMerchantImmediateDiscount)
        const managerImmediateCoupon = find(immediatedCoupons, eqManagerImmediateDiscount)

        const [
            merchantProductCouponList,
            managerProductCouponList,
            selectedMerchantImmediateCoupon,
            selectedMerchantProductCoupon,
            selectedManagerImmediateCoupon,
            selectedManagerProductCoupon,
        ] = [
            /** 상품할인 쿠폰은 복수개 존재 가능하므로 filter로 탐색 */
            productCoupons.filter(eqMerchantProductDiscount),
            productCoupons.filter(eqManagerProductDiscount),
            /**
             * 선택된 판매자상품 할인은 상품할인/즉시할인 중 하나만 선택가능하므로
             * selected{*}immediateCoupon / selected{*}productCoupon 중 최대 한개만 존재가능
             */
            find(selectedImmediateCoupons, eqMerchantImmediateDiscount),
            find(selectedProductCoupons, eqMerchantProductDiscount),
            find(selectedImmediateCoupons, eqManagerImmediateDiscount),
            find(selectedProductCoupons, eqManagerProductDiscount),
        ]

        /** 즉시할인 쿠폰과 일반 쿠폰의 interface가 다르므로 convert 필요 */
        const convertedMerchantImmediateCoupon = merchantImmediateCoupon
            ? convertCouponByImmediateCoupon(merchantImmediateCoupon)
            : undefined

        const convertedManagerImmediateCoupon = managerImmediateCoupon
            ? convertCouponByImmediateCoupon(managerImmediateCoupon)
            : undefined

        /**
         * 즉시할인 쿠폰에 대해 쿠폰 할인액 계산
         */
        const itemInfo = orderSheetItemFlatMap[orderSheetItemId]
        const unitPrice = itemInfo?.unitPrice ?? 0
        const quantity = itemInfo?.quantity ?? 0

        const {discountUnit, immediateDiscountAmount: merchantImmediateDiscountAmount} = getImmediateDiscountAmount({
            immediateDiscount: convertedMerchantImmediateCoupon,
            unitPrice,
            quantity,
        })

        const discountAmount =
            selectedMerchantImmediateCoupon && equals(selectedMerchantImmediateCoupon, merchantImmediateCoupon)
                ? discountUnit
                : 0

        const {immediateDiscountAmount: managerImmediateDiscountAmount} = getImmediateDiscountAmount({
            immediateDiscount: convertedManagerImmediateCoupon,
            // 네이버 추가할인 즉시할인 금액을 노출할때, 적용된 할인 금액을 반영한후 금액 계산
            unitPrice: unitPrice - discountAmount,
            quantity,
        })

        const merchant = getProductCouponDiscount({
            immediateCoupon: merchantImmediateCoupon,
            immediateDiscountAmount: merchantImmediateDiscountAmount,
            productCouponList: merchantProductCouponList,
            selectedImmediateCoupon: selectedMerchantImmediateCoupon,
            selectedProductCoupon: selectedMerchantProductCoupon,
            itemId: orderSheetItemId,
            couponName: '상품 할인',
            isImmediateDiscount: isMerchantImmediateDiscount,
            isProductDiscount: isMerchantProductDiscount,
            getSelectedCouponDiscountAmount,
        })
        const manager = getProductCouponDiscount({
            immediateCoupon: managerImmediateCoupon,
            immediateDiscountAmount: managerImmediateDiscountAmount,
            productCouponList: managerProductCouponList,
            selectedImmediateCoupon: selectedManagerImmediateCoupon,
            selectedProductCoupon: selectedManagerProductCoupon,
            itemId: orderSheetItemId,
            isImmediateDiscount: isManagerImmediateDiscount,
            isProductDiscount: isManagerProductDiscount,
            getSelectedCouponDiscountAmount,
        })

        return {
            ...acc,
            [orderSheetItemId]: {
                merchant,
                manager,
            },
        }
    }, {})
}

export const getDeliveryDiscount = ({
    deliveryCoupons,
    deliveryGroupsByKey,
}: {
    deliveryCoupons: {[x: string]: OrderSheetCoupon | null}
    deliveryGroupsByKey: {[deliveryGroupKey: string]: DeliveryGroup}
}) => {
    return mapValueKeys((coupon, deliveryGroupKey) => {
        const {deliveryFee, additionalDeliveryFee} = deliveryGroupsByKey[deliveryGroupKey] ?? {
            deliveryFee: 0,
            additionalDeliveryFee: 0,
        }
        return getCouponDiscountValue(coupon, deliveryFee + additionalDeliveryFee)
    }, deliveryCoupons)
}

/**
 * 스토어쿠폰을 적용할 수 있는 상품금액 총합을 구하는 함수
 * ex) A스토어에서 상품1, 상품2, 상품3 주문
 * 상품1, 상품2 만 스토어할인을 적용가능하다면
 * 상품1, 상품2 의 금액만 더해서 반환
 * +) 스토어쿠폰은 추가상품가격도 최소사용금액에 포함됨
 */
export const getMerchantOrderAmount = ({
    productsByMerchantNo,
    productCouponDiscountAmountByItemId,
    storeCoupons,
}: {
    productsByMerchantNo: {[merchantNo: number]: OrderSheetProductView[]}
    productCouponDiscountAmountByItemId: {[x: string]: number}
    storeCoupons: {[x: number]: (OrderSheetCoupon & {disabled: boolean})[]}
}) => {
    const merchantOrderAmountByItemId: {
        [merchantNo: number]: {
            [orderSheetItemId: string]: number
        }
    } = mapValueKeys((products) => {
        return products.reduce<Record<string, number>>((acc, {items, type}) => {
            items.forEach((item) => {
                if (!ExceptionProductTypesByStoreCoupon.includes(type)) {
                    acc[item.orderSheetItemId] =
                        item.orderAmount - (productCouponDiscountAmountByItemId[item.orderSheetItemId] ?? 0)
                }
            })
            return acc
        }, {})
    }, productsByMerchantNo)

    const merchantTargetItemOrderAmountByCoupon: {
        [merchantNo: number]: {
            [couponId: string]: number
        }
    } = mapValueKeys((coupons, merchantNo) => {
        return coupons.reduce<Record<string, number>>((acc, coupon) => {
            const targetIds = coupon.targetOrderSheetItemIds ?? []
            const couponId = coupon.couponId
            const targetOrderAmount = targetIds.reduce((amtAcc, targetId) => {
                return amtAcc + (merchantOrderAmountByItemId?.[merchantNo]?.[targetId] ?? 0)
            }, 0)
            acc[couponId] = targetOrderAmount
            return acc
        }, {})
    }, storeCoupons)

    return merchantTargetItemOrderAmountByCoupon
}

export const getDuplicatedProductCoupon = ({
    duplicatedCouponsByItemId,
    selectedDuplicatedCouponsByItemId,
    getSelectedCouponDiscountAmount,
}: {
    duplicatedCouponsByItemId: {[x: string]: (OrderSheetCoupon & {disabled: boolean})[]}
    selectedDuplicatedCouponsByItemId: {[OrderSheetItemId: string]: OrderSheetCoupon[]}
    getSelectedCouponDiscountAmount: (
        itemId: string | number,
        couponTypeCheckFunction: (couponType: DiscountCouponType) => boolean,
    ) => number
}) => {
    return mapValueKeys((coupons, itemId) => {
        const isDuplicatedProductDiscount = eq(DiscountCoupon.DUPLICATE_PRODUCT_DISCOUNT)

        const selectedDuplicateCoupon = head(selectedDuplicatedCouponsByItemId[itemId])

        return {
            coupons: coupons.map((coupon) => {
                const {discountUnitType, discountValue} = coupon
                return {
                    ...coupon,
                    discountAmount: discountUnitType === 'AMOUNT' ? discountValue : undefined,
                }
            }),
            selectedCoupon: selectedDuplicateCoupon
                ? {
                      ...selectedDuplicateCoupon,
                      discountAmount: getSelectedCouponDiscountAmount(itemId, isDuplicatedProductDiscount),
                  }
                : null,
        }
    }, duplicatedCouponsByItemId)
}

export const getDeliveryCouponDiscount = ({
    deliveryCouponByDeliveryGroupKey,
    selectedDeliveryCouponByDeliveryGroupKey,
    deliveryItem,
}: {
    deliveryCouponByDeliveryGroupKey: {[x: string]: (OrderSheetCoupon & {disabled: boolean})[]}
    selectedDeliveryCouponByDeliveryGroupKey: {[x: string]: OrderSheetCoupon | null}
    deliveryItem?: {[deliveryGroupKey: string]: OrderSheetDiscountItem}
}) => {
    return mapValueKeys((coupons, deliveryGroupKey) => {
        const selectedCoupon = selectedDeliveryCouponByDeliveryGroupKey[String(deliveryGroupKey)]

        return {
            coupons: coupons.map((coupon) => ({...coupon, discountAmount: coupon.discountValue})),
            selectedCoupon: selectedCoupon
                ? {
                      ...selectedCoupon,
                      discountAmount: deliveryItem
                          ? (deliveryItem[deliveryGroupKey]?.discountAmount ?? 0)
                          : selectedCoupon.discountValue,
                  }
                : null,
        }
    }, deliveryCouponByDeliveryGroupKey)
}

/**
 * @description
 * 스토어할인쿠폰 disabled 처리 함수
 * @params merchantOrderAmount
 * 판매자별 스토어쿠폰 적용가능한 상품금액의 합
 */
export const getStoreCouponDiscountMap = ({
    storeCoupons,
    selectedStoreCoupons,
    merchantOrderAmount,
}: {
    storeCoupons: {[x: number]: (OrderSheetCoupon & {disabled: boolean})[]}
    selectedStoreCoupons: {
        [MerchantNo: number]: Maybe<OrderSheetCoupon>
    }
    merchantOrderAmount: {
        [merchantNo: number]: {
            [couponId: string]: number
        }
    }
}) => {
    const storeCouponDiscountMap = mapValueKeys((coupons, merchantNo) => {
        return coupons.map((coupon) => {
            return {
                ...coupon,
                // 쿠폰 최소 금액이 부족할경우 disabled
                disabled:
                    coupon.disabled ||
                    (merchantOrderAmount[merchantNo]?.[coupon.couponId] ?? 0) === 0 ||
                    (!isUndefined(merchantOrderAmount[merchantNo]) &&
                        (coupon.minOrderAmount ?? 0) > (merchantOrderAmount[merchantNo]?.[coupon.couponId] ?? 0)),
            }
        })
    }, storeCoupons)
    return {
        storeCouponDiscountMap,
        hasSelectedDisableCoupon: someValueKeys((coupons, merchantNo) => {
            return coupons.some(
                ({couponId, disabled}) => disabled && selectedStoreCoupons[merchantNo]?.couponId === couponId,
            )
        }, storeCouponDiscountMap ?? {}),
        filteredStoreCouponsByDisabled: mapValueKeys((selectedCoupon, merchantNo) => {
            if (
                selectedCoupon &&
                !(
                    storeCouponDiscountMap &&
                    storeCouponDiscountMap[merchantNo] &&
                    storeCouponDiscountMap[merchantNo].some(
                        ({couponId, disabled}) => disabled && selectedStoreCoupons[merchantNo]?.couponId === couponId,
                    )
                )
            ) {
                return selectedCoupon
            }
            return null
        }, selectedStoreCoupons),
    }
}

export const getStoreCouponDiscount = ({
    getSelectedCouponDiscountAmount,
    productsByMerchantNo,
    storeCouponDiscountMap,
    selectedStoreCouponByMerchantNo,
}: {
    getSelectedCouponDiscountAmount: (
        itemId: string | number,
        couponTypeCheckFunction: (couponType: DiscountCouponType) => boolean,
    ) => number
    productsByMerchantNo: {[merchantNo: number]: OrderSheetProductView[]}
    storeCouponDiscountMap: {[x: number]: (OrderSheetCoupon & {disabled: boolean})[]}
    selectedStoreCouponByMerchantNo: {[MerchantNo: number]: Maybe<OrderSheetCoupon>}
}) => {
    /**
     * BE에서 받아온 선택된 스토어 쿠폰의 적용금액들의 총합을 합산하는 함수
     * store coupon의 할인액이 itemId별로 오기때문에 merchantNo에 해당하는 product들의 itemId들의 coupon 할인액 총합을 구함
     */
    const getSelectedStoreCouponDiscountAmountByMerchantNo = (merchantNo: number): number => {
        const isStoreCoupon = eq(DiscountCoupon.STORE_DISCOUNT)

        const storeProducts = productsByMerchantNo[merchantNo]
        return (
            storeProducts?.reduce((acc, {items}) => {
                const storeCouponDiscountAmountByItemId = items.reduce(
                    (itemsAcc, {orderSheetItemId}) =>
                        itemsAcc + getSelectedCouponDiscountAmount(orderSheetItemId, isStoreCoupon),
                    0,
                )

                return acc + storeCouponDiscountAmountByItemId
            }, 0) ?? 0
        )
    }

    return mapValueKeys((coupons, merchantNo) => {
        const selectedStoreCoupon = selectedStoreCouponByMerchantNo[merchantNo]

        return {
            coupons: coupons.map((coupon) => {
                const {discountUnitType, discountValue} = coupon
                return {
                    ...coupon,
                    discountAmount: discountUnitType === 'AMOUNT' ? discountValue : undefined,
                }
            }),
            selectedCoupon: selectedStoreCoupon
                ? {
                      ...selectedStoreCoupon,
                      discountAmount: getSelectedStoreCouponDiscountAmountByMerchantNo(merchantNo),
                  }
                : null,
        }
    }, storeCouponDiscountMap)
}

type RefinedOrderSheetCoupon = OrderSheetCoupon & {discountAmount?: number}

interface CouponDiscountInfoByKey {
    [key: string]: {
        coupons: RefinedOrderSheetCoupon[]
        selectedCoupon: Maybe<RefinedOrderSheetCoupon>
    }
}

interface ImmediateCouponContainedCouponInfo {
    merchantProductCouponDiscount: CouponDiscountInfoByKey
    managerProductCouponDiscount: CouponDiscountInfoByKey
}

export const getMerchantAndMangerCouponDiscount = ({
    immediateCouponContainedCouponInfoByItemId,
}: {
    immediateCouponContainedCouponInfoByItemId: {
        [x: string]: {
            merchant: {
                coupons: RefinedOrderSheetCoupon[]
                selectedCoupon: Maybe<RefinedOrderSheetCoupon>
            }
            manager: {
                coupons: RefinedOrderSheetCoupon[]
                selectedCoupon: Maybe<RefinedOrderSheetCoupon>
            }
        }
    }
}) => {
    /**
     * commonProductCouponDiscount 는 itemId별로 merchantProductCouponDiscount, managerProductCouponDiscount가 혼재
     * itemId별 merchantProductCouponDiscount, managerProductCouponDiscount를 분리
     */
    const {merchantProductCouponDiscount, managerProductCouponDiscount} = Object.entries(
        immediateCouponContainedCouponInfoByItemId,
    ).reduce<ImmediateCouponContainedCouponInfo>(
        (acc, [itemId, {merchant, manager}]) => {
            return {
                merchantProductCouponDiscount: {
                    ...acc.merchantProductCouponDiscount,
                    [itemId]: merchant,
                },
                managerProductCouponDiscount: {
                    ...acc.managerProductCouponDiscount,
                    [itemId]: manager,
                },
            }
        },
        {merchantProductCouponDiscount: {}, managerProductCouponDiscount: {}},
    )

    return {
        merchantProductCouponDiscount,
        managerProductCouponDiscount,
    }
}
